import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { log } from '../Helper';

/**
 * Custom hook for filtering data based on COMAR numbers or buildId-COMAR combinations
 * 
 * FLOW EXPLANATION:
 * 1. When user clicks on urgent action tiles (Missing Shipping Order, Late Pickup, etc.)
 * 2. urgentActions/index.jsx calls history.push() with filter data in navigation state
 * 3. User is redirected to target page (load-build or avis)
 * 4. This hook detects navigation state containing filter data
 * 5. Hook automatically applies the filter to the page's data
 * 6. FilterHeader component shows active filter with clear option
 * 
 * USAGE:
 * - Import this hook in pages that need COMAR-based filtering (load-build, avis)
 * - Pass original data and field name for COMAR identification
 * - Hook returns filtered data and filter state management functions
 * 
 * @param {Array} originalData - The complete dataset to filter
 * @param {string} comarFieldName - Field name containing COMAR numbers (default: 'comarNo')
 * @returns {Object} Filter state and filtered data
 */
export const useComarFilter = (originalData, comarFieldName = 'comarNo') => {
  const location = useLocation();
  
  // State variables for managing filter functionality
  const [comarFilter, setComarFilter] = useState([]); // Array of COMAR numbers or buildId-COMAR objects to filter by
  const [isFiltered, setIsFiltered] = useState(false); // Boolean flag indicating if filter is active
  const [filterSource, setFilterSource] = useState(''); // Source of the filter (for tracking/debugging)
  const [filterTitle, setFilterTitle] = useState(''); // Display title for the active filter
  const [totalFilteredOrders, setTotalFilteredOrders] = useState(0); // Count of filtered orders
  const [filteredData, setFilteredData] = useState([]); // The actual filtered dataset

  // NAVIGATION STATE DETECTION: 
  // Detects when user navigates from urgent action tiles with filter data
  // This is triggered when tiles call history.push(path, navigationState)
  useEffect(() => {
    log('[useComarFilter] Navigation state detected:', location.state);
    
    // Check if navigation state contains filter data from urgent action tiles
    if (location.state?.filterComar && location.state?.autoApplyFilter) {
      log('[useComarFilter] Applying filter from navigation state:', {
        filterComar: location.state.filterComar,
        filterSource: location.state.filterSource,
        filterTitle: location.state.filterTitle,
        totalOrders: location.state.totalOrders,
        filterType: location.state.filterType
      });
      
      // Extract filter configuration from navigation state
      setComarFilter(location.state.filterComar); // Array of COMAR numbers or buildId-COMAR objects
      setFilterSource(location.state.filterSource || 'External Filter'); // Track filter origin
      setFilterTitle(location.state.filterTitle || 'Filtered Orders'); // Display name (e.g., 'LATE PICKUP')
      setTotalFilteredOrders(location.state.totalOrders || 0); // Count for display
      setIsFiltered(true); // Activate filter state
      
      // Clear navigation state to prevent re-applying filter on page refresh
      // This ensures filter is only applied once per navigation
      window.history.replaceState({}, document.title);
      log('[useComarFilter] Filter applied successfully, navigation state cleared');
    }
  }, [location.state]); // Re-run when location state changes (new navigation)

  // DATA FILTERING ENGINE:
  // Applies the filter to the original data based on filter type
  useEffect(() => {
    log('[useComarFilter] Data filtering effect triggered:', {
      comarFilterLength: comarFilter.length,
      originalDataLength: originalData.length,
      comarFieldName,
      filterSource
    });
    
    if (comarFilter.length > 0 && originalData.length > 0) {
      // ADVANCED FILTERING: Check if this is a buildId-COMAR combination filter
      // Used for AVIS orders where we need to match both buildId and COMAR
      if (location.state?.filterType === 'buildIdComar' && Array.isArray(comarFilter) && 
          comarFilter.length > 0 && typeof comarFilter[0] === 'object' && 
          comarFilter[0].hasOwnProperty('buildId')) {
        
        log('[useComarFilter] Using buildId-COMAR filter type:', comarFilter);
        // Apply complex buildId-COMAR filtering (for VAT, delivery date missing, etc.)
        applyBuildIdComarFilter(comarFilter, originalData);
      } else {
        log('[useComarFilter] Using simple COMAR filter:', comarFilter);
        // SIMPLE FILTERING: Standard COMAR-only filtering
        // Used for load-build orders where we only match COMAR numbers
        const filtered = originalData.filter(row => 
          comarFilter.includes(row[comarFieldName])
        );
        log('[useComarFilter] Filtered result count:', filtered.length);
        setFilteredData(filtered);
      }
    } else {
      log('[useComarFilter] No filter active, returning all data');
      // No filter active: return all original data
      setFilteredData(originalData);
    }
  }, [comarFilter, originalData, comarFieldName, filterSource, location.state]); // Re-run when dependencies change

  // COMPLEX FILTERING FUNCTION:
  // Handles mixed buildId and COMAR filtering for AVIS orders
  // This is needed because some urgent actions are identified by buildId, others by COMAR
  const applyBuildIdComarFilter = (buildIdWithComarList, dataToFilter) => {
    log('[useComarFilter] Applying buildId-COMAR filter:', {
      filterCount: buildIdWithComarList.length,
      dataCount: dataToFilter.length
    });
    
    const filtered = dataToFilter.filter(row => {
      // Extract row data for comparison
      const rowBuildId = parseInt(row.buildId) || 0; // Convert to int, default to 0 for null/undefined
      const rowZComar = row.zcomar || row.comarNo; // AVIS uses zcomar field, fallback to comarNo
      
      // Check each filter criteria from urgent action tiles
      for (let filterItem of buildIdWithComarList) {
        const filterBuildId = parseInt(filterItem.buildId) || 0; // Filter buildId
        const filterComar = filterItem.comar; // Filter COMAR number
        
        // CASE 1: Filter has valid buildId (orders with load builds)
        // Match rows that have the same buildId
        if (filterBuildId && filterBuildId !== 0) {
          if (rowBuildId && rowBuildId === filterBuildId) {
            log('[useComarFilter] Match found by buildId:', {
              rowBuildId,
              filterBuildId,
              rowZComar
            });
            return true; // Include this row in filtered results
          }
        }
        // CASE 2: Filter buildId is 0 (orders without load builds)
        // Match rows by COMAR number only
        else if (filterBuildId === 0 && filterComar) {
          // Check if row also has no buildId (0 or null), then match by COMAR
          if (rowBuildId === 0 && rowZComar === filterComar) {
            log('[useComarFilter] Match found by COMAR (no buildId):', {
              rowBuildId,
              rowZComar,
              filterComar
            });
            return true; // Include this row in filtered results
          }
        }
      }
      
      return false; // Exclude this row from filtered results
    });

    log('[useComarFilter] BuildId-COMAR filter complete. Filtered count:', filtered.length);
    setFilteredData(filtered); // Update the filtered dataset
  };

  // FILTER CLEANUP:
  // Clears all filter state and returns to showing all data
  // Called when user clicks the clear filter button in FilterHeader component
  const clearFilter = () => {
    log('[useComarFilter] Clearing filter. Current state:', {
      comarFilterLength: comarFilter.length,
      isFiltered,
      filterSource,
      filterTitle,
      totalFilteredOrders
    });
    
    // Reset all filter-related state to initial values
    setComarFilter([]); // Clear the filter criteria
    setIsFiltered(false); // Deactivate filter state
    setFilterSource(''); // Clear filter source tracking
    setFilterTitle(''); // Clear filter display title
    setTotalFilteredOrders(0); // Reset count
    setFilteredData(originalData); // Show all original data
    
    // Clear navigation state to prevent filter from re-applying
    window.history.replaceState({}, document.title);
    log('[useComarFilter] Filter cleared successfully');
  };

  // HOOK RETURN VALUES:
  // Everything the consuming component needs for filter functionality
  return {
    comarFilter, // Current filter criteria (for debugging/tracking)
    isFiltered, // Boolean flag for showing/hiding FilterHeader component
    filterSource, // Source of filter (for tracking/debugging)
    filterTitle, // Display title for FilterHeader (e.g., 'LATE PICKUP')
    totalFilteredOrders, // Count for display in FilterHeader
    filteredData, // The actual filtered dataset to display in tables
    clearFilter // Function to clear the filter (passed to FilterHeader)
  };
};
